import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "@/components/PageHeader";
import {
  getRacingLiveSessions,
  getRacingPilotBest,
  getRacingRankings,
  type RacingLiveSession,
  type RacingPilotBestOut,
} from "@/api/racing";
import { buildEmptyRacingBasicsDataset } from "@/live-racing/build-dataset";
import { formatMs, formatSector } from "@/live-racing/race-engine";
import { LiveRaceStage } from "@/live-racing/LiveRaceStage";
import { useLiveRacing } from "@/live-racing/useLiveRacing";

import "@/styles/live-racing.css";

type Tab = "live" | "rankings";

export function LiveRacingPage() {
  const [tab, setTab] = useState<Tab>("live");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const sessionsQuery = useQuery({
    queryKey: ["racing", "live-sessions"],
    queryFn: getRacingLiveSessions,
    refetchInterval: 4000,
  });

  const rankingsQuery = useQuery({
    queryKey: ["racing", "rankings"],
    queryFn: () => getRacingRankings(10),
  });

  const sessions = sessionsQuery.data?.sessions ?? [];

  // Auto-pick the first session whenever the list changes.
  useEffect(() => {
    if (!sessions.length) {
      setSelectedKey(null);
      return;
    }
    const existing = sessions.find((s) => sessionKeyOf(s) === selectedKey);
    if (!existing) setSelectedKey(sessionKeyOf(sessions[0]));
  }, [sessions, selectedKey]);

  const selectedSession = useMemo(
    () => sessions.find((s) => sessionKeyOf(s) === selectedKey) ?? null,
    [sessions, selectedKey],
  );

  const live = useLiveRacing(selectedSession);

  const pilotBestQuery = useQuery({
    queryKey: ["racing", "pilot-best", selectedSession?.user_id ?? null],
    queryFn: () => getRacingPilotBest(selectedSession!.user_id),
    enabled: !!selectedSession,
  });

  return (
    <div className="live-racing-root">
      <PageHeader
        title="Live Data [ALPHA]"
        lede="Real-time Racing — Basics telemetry with sector timing. Only available while a pilot is actively streaming from the plugin."
        actions={
          <div className="lr-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className={`lr-tab${tab === "live" ? " active" : ""}`}
              onClick={() => setTab("live")}
            >
              LIVE STAGE
            </button>
            <button
              type="button"
              role="tab"
              className={`lr-tab${tab === "rankings" ? " active" : ""}`}
              onClick={() => setTab("rankings")}
            >
              TOP TIMES
            </button>
          </div>
        }
      />

      {tab === "live" ? (
        <LiveView
          sessions={sessions}
          loading={sessionsQuery.isLoading}
          available={sessionsQuery.data?.available ?? true}
          selectedSession={selectedSession}
          onSelect={setSelectedKey}
          datasetReady={!!live.dataset}
          live={live}
          pilotBest={pilotBestQuery.data ?? null}
        />
      ) : (
        <RankingsView
          isLoading={rankingsQuery.isLoading}
          isError={rankingsQuery.isError}
          rows={rankingsQuery.data?.rows ?? []}
          bestSectors={rankingsQuery.data?.best_sectors ?? []}
        />
      )}
    </div>
  );
}

function sessionKeyOf(session: RacingLiveSession) {
  return `${session.user_id}|${session.key}`;
}

interface LiveViewProps {
  sessions: RacingLiveSession[];
  loading: boolean;
  available: boolean;
  selectedSession: RacingLiveSession | null;
  onSelect: (key: string | null) => void;
  datasetReady: boolean;
  live: ReturnType<typeof useLiveRacing>;
  pilotBest: RacingPilotBestOut | null;
}

function LiveView({
  sessions,
  loading,
  available,
  selectedSession,
  onSelect,
  datasetReady,
  live,
  pilotBest,
}: LiveViewProps) {
  if (loading) return <p className="empty-state">Scanning for live sessions…</p>;
  if (!available) {
    return (
      <div className="callout warn">
        <strong>Live store unavailable</strong>
        <p>The Redis websocket buffer is offline — live telemetry cannot be surfaced.</p>
      </div>
    );
  }

  if (!sessions.length) {
    const emptyDataset = buildEmptyRacingBasicsDataset();
    return (
      <>
        <div className="lr-no-signal-banner">
          <span className="lr-no-signal-dot" />
          <span className="lr-no-signal-text">
            <strong>NO LIVE SESSION</strong>
            <small>Fire up the plugin and start flying Racing — Basics. Other missions are ignored in the ALPHA.</small>
          </span>
        </div>
        <LiveRaceStage
          dataset={emptyDataset}
          currentT={0}
          statusLabel="NO SIGNAL"
          statusMode="inactive"
        />
      </>
    );
  }

  const statusLabel = live.status === "live"
    ? "LIVE"
    : live.status === "inactive"
      ? "IDLE"
      : live.status === "error"
        ? "ERROR"
        : "CONNECTING";
  const statusMode = live.status === "live" ? "live" : live.status === "error" ? "inactive" : "replay";

  return (
    <>
      <div className="lr-session-strip">
        {sessions.map((s) => {
          const k = sessionKeyOf(s);
          const active = selectedSession && sessionKeyOf(selectedSession) === k;
          return (
            <button
              key={k}
              type="button"
              className={`lr-session-chip${active ? " active" : ""}`}
              onClick={() => onSelect(k)}
            >
              <span className="lr-session-chip-user">
                {s.username ?? `pilot #${s.user_id}`}
              </span>
              <span className="lr-session-chip-meta">
                {s.flight_frame_count.toLocaleString()} frames · {s.key.replace("VBS4_Session_", "")}
              </span>
            </button>
          );
        })}
      </div>

      {!selectedSession ? (
        <p className="empty-state">Pick a live session above to open the tactical map.</p>
      ) : live.error ? (
        <div className="callout danger">
          <strong>Could not open live session</strong>
          <p>{live.error}</p>
        </div>
      ) : !datasetReady ? (
        <p className="empty-state">Loading initial frames…</p>
      ) : live.dataset ? (
        <LiveRaceStage
          dataset={live.dataset}
          currentT={live.latestT}
          statusLabel={statusLabel}
          statusMode={statusMode as "live" | "inactive" | "replay"}
          pilotBest={pilotBest}
        />
      ) : null}
    </>
  );
}

interface RankingsViewProps {
  isLoading: boolean;
  isError: boolean;
  rows: import("@/api/racing").RacingRankingRow[];
  bestSectors: import("@/api/racing").RacingBestSector[];
}

function RankingsView({ isLoading, isError, rows, bestSectors }: RankingsViewProps) {
  if (isLoading) return <p className="empty-state">Loading rankings…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load rankings</strong>
      </div>
    );
  }

  const nSectors = Math.max(
    bestSectors.length,
    ...rows.map((r) => r.sectors.length),
  );

  return (
    <section className="live-racing-root">
      <div className="lr-rankings-table-wrap">
        <table className="lr-rankings-table">
          <thead>
            <tr>
              <th className="lr-col-pos">#</th>
              <th>Pilot</th>
              <th className="lr-col-time">Best lap</th>
              {Array.from({ length: nSectors }, (_, i) => i + 1).map((i) => (
                <th key={i} className="lr-col-sector">
                  S{i}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3 + nSectors} className="lr-empty-cell">
                  No lap times recorded yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const sectorByIdx = new Map(
                  row.sectors.map((s) => [s.sector_index, s.duration_seconds]),
                );
                return (
                  <tr key={`${row.user_id}-${row.best_lap_flight_id}`}>
                    <td className="lr-col-pos">{row.rank}</td>
                    <td>
                      <strong>{row.username}</strong>
                      {row.best_lap_session_name ? (
                        <div className="lr-subline">
                          {row.best_lap_session_name}
                        </div>
                      ) : null}
                    </td>
                    <td className="lr-col-time">
                      {formatMs(row.best_lap_seconds * 1000)}
                    </td>
                    {Array.from({ length: nSectors }, (_, i) => i + 1).map((i) => {
                      const sec = sectorByIdx.get(i);
                      const best = bestSectors.find((b) => b.sector_index === i);
                      const isTrackBest =
                        best && sec !== undefined && Math.abs(sec - best.duration_seconds) < 1e-6;
                      return (
                        <td key={i} className={`lr-col-sector${isTrackBest ? " lr-track-best" : ""}`}>
                          {sec !== undefined ? `${formatSector(sec * 1000)}s` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <section className="lr-best-sector-grid-wrap">
        <h3 className="lr-section-title lr-best-sector-title">Track sector records</h3>
        <div className="lr-best-sector-cards">
          {bestSectors.length === 0 ? (
            <p className="empty-state">No sector records yet.</p>
          ) : (
            bestSectors.map((b) => (
              <div key={b.sector_index} className="lr-best-sector-card">
                <div className="lr-s-head">
                  <span className="lr-s-name">S{b.sector_index}</span>
                  <span className="lr-s-gates">
                    {b.username ?? (b.user_id !== null ? `pilot #${b.user_id}` : "—")}
                  </span>
                </div>
                <div className="lr-s-time">{formatSector(b.duration_seconds * 1000)}s</div>
                {b.flight_id !== null ? (
                  <div className="lr-subline">flight #{b.flight_id}</div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </section>
    </section>
  );
}
